import { useEffect, useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function Resources() {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': 'Resources | Insights & Videos | The CROWN Abundance Movement',
    'description': 'Explore The CROWN Abundance Movement resource library featuring transformational insights, articles, videos, and masterclasses on wealth mastery, relationship wholeness, and generational legacy.',
    'url': `${baseUrl}/resources`,
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': [
        {
          '@type': 'VideoObject',
          'name': 'Introduction to The CROWN Abundance Movement',
          'description': 'Discover the vision, mission, and core philosophy behind The CROWN Abundance Movement',
          'thumbnailUrl': 'https://public.readdy.ai/ai/img_res/f96e8404461a6232bc9f277bb07bb5ed.jpg'
        },
        {
          '@type': 'Article',
          'headline': 'The Five Pillars of Wealth Consciousness',
          'description': 'Explore the fundamental mindset shifts required to build sustainable wealth and financial freedom'
        }
      ]
    }
  };

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionForm, setSubmissionForm] = useState({
    name: '',
    email: '',
    contentType: 'video',
    category: 'wealth',
    title: '',
    description: '',
    url: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    document.title = 'Resources | Insights & Videos | The CROWN Abundance Movement';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore The CROWN Abundance Movement resource library featuring transformational insights, articles, videos, and masterclasses on wealth mastery, relationship wholeness, and generational legacy.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'CROWN resources, wealth mastery insights, relationship articles, legacy building, masterclasses, videos, transformational content');
    }
  }, []);

  const categories = [
    { id: 'all', name: 'All Topics', icon: 'ri-grid-line' },
    { id: 'wealth', name: 'Wealth Mastery', icon: 'ri-money-dollar-circle-line' },
    { id: 'relationships', name: 'Relationships', icon: 'ri-heart-line' },
    { id: 'legacy', name: 'Legacy', icon: 'ri-parent-line' },
    { id: 'testimonials', name: 'Testimonials', icon: 'ri-star-line' }
  ];

  const types = [
    { id: 'all', name: 'All Resources', icon: 'ri-folder-line' },
    { id: 'video', name: 'Videos', icon: 'ri-video-line' },
    { id: 'insight', name: 'Insights', icon: 'ri-article-line' }
  ];

  const resources = [
    {
      id: 1,
      title: 'Introduction to The CROWN Abundance Movement',
      description: 'Discover the vision, mission, and core philosophy behind The CROWN Abundance Movement and how it can transform your life.',
      category: 'all',
      type: 'video',
      duration: '12:45',
      thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20African%20American%20speaker%20on%20stage%20at%20luxury%20conference%20with%20golden%20lighting%2C%20elegant%20diverse%20audience%2C%20wealth%20and%20success%20theme%2C%20high-end%20event%20atmosphere&width=800&height=450&seq=video-intro&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      views: '15.2K',
      date: 'Dec 2024'
    },
    {
      id: 2,
      title: 'Money: The Tool for Dominion - Book Overview',
      description: 'An in-depth look at the foundational principles of wealth as a tool for purpose, not self-indulgence.',
      category: 'wealth',
      type: 'video',
      duration: '18:30',
      thumbnail: 'https://readdy.ai/api/search-image?query=Luxury%20book%20presentation%20with%20golden%20accents%2C%20elegant%20desk%20setup%2C%20wealth%20and%20prosperity%20symbols%2C%20sophisticated%20business%20environment%20with%20African%20American%20professional&width=800&height=450&seq=video-book&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      views: '22.8K',
      date: 'Nov 2024'
    },
    {
      id: 3,
      title: 'Building Relationship Capital',
      description: 'Learn how healthy relationships serve as a form of capital and fuel personal and professional growth.',
      category: 'relationships',
      type: 'video',
      duration: '15:20',
      thumbnail: 'https://readdy.ai/api/search-image?query=Elegant%20diverse%20couples%20in%20luxury%20counseling%20room%2C%20warm%20atmosphere%2C%20relationship%20healing%20and%20connection%2C%20sophisticated%20wellness%20environment&width=800&height=450&seq=video-relationship&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      views: '18.5K',
      date: 'Nov 2024'
    },
    {
      id: 4,
      title: 'The Five Pillars of Wealth Consciousness',
      description: 'Explore the fundamental mindset shifts required to build sustainable wealth and financial freedom. Learn how to transform your relationship with money from scarcity to abundance.',
      category: 'wealth',
      type: 'insight',
      readTime: '8 min read',
      thumbnail: 'https://readdy.ai/api/search-image?query=African%20American%20business%20professional%20reading%20financial%20documents%20in%20elegant%20office%2C%20golden%20accents%2C%20wealth%20consciousness%2C%20sophisticated%20business%20setting&width=800&height=450&seq=insight-wealth&orientation=landscape',
      views: '12.3K',
      date: 'Jan 2025'
    },
    {
      id: 5,
      title: 'Generational Legacy: Building What Lasts',
      description: 'Discover how to intentionally build a legacy that transcends individual success and benefits future generations.',
      category: 'legacy',
      type: 'video',
      duration: '20:15',
      thumbnail: 'https://readdy.ai/api/search-image?query=Multi-generational%20African%20American%20family%20in%20elegant%20library%2C%20grandfather%20teaching%20young%20adults%2C%20wisdom%20transfer%2C%20sophisticated%20family%20legacy%20atmosphere&width=800&height=450&seq=video-legacy&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      views: '25.3K',
      date: 'Oct 2024'
    },
    {
      id: 6,
      title: 'Healing Relationship Wounds: A Path to Wholeness',
      description: 'Understand the patterns that keep relationships broken and discover practical steps toward healing, forgiveness, and restoration in your most important connections.',
      category: 'relationships',
      type: 'insight',
      readTime: '10 min read',
      thumbnail: 'https://readdy.ai/api/search-image?query=Diverse%20couple%20in%20peaceful%20counseling%20session%2C%20warm%20lighting%2C%20healing%20atmosphere%2C%20elegant%20wellness%20space%20with%20African%20American%20therapist&width=800&height=450&seq=insight-relationship&orientation=landscape',
      views: '9.8K',
      date: 'Jan 2025'
    },
    {
      id: 7,
      title: 'Founders Forum Success Stories',
      description: 'Hear from successful business leaders who have transformed their enterprises through CROWN principles.',
      category: 'testimonials',
      type: 'video',
      duration: '14:50',
      thumbnail: 'https://readdy.ai/api/search-image?query=Successful%20diverse%20business%20leaders%20in%20luxury%20boardroom%2C%20celebrating%20achievements%2C%20golden%20accents%2C%20high-end%20corporate%20success%20atmosphere&width=800&height=450&seq=video-testimonial&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      views: '12.7K',
      date: 'Oct 2024'
    },
    {
      id: 8,
      title: 'Creating Generational Wealth in South Africa',
      description: 'A comprehensive guide to building lasting wealth in the South African context. Learn investment strategies, tax optimization, and legacy planning specific to RSA.',
      category: 'legacy',
      type: 'insight',
      readTime: '12 min read',
      thumbnail: 'https://readdy.ai/api/search-image?query=African%20American%20family%20reviewing%20financial%20documents%20together%2C%20elegant%20home%20office%2C%20South%20African%20context%2C%20generational%20wealth%20planning%20atmosphere&width=800&height=450&seq=insight-legacy&orientation=landscape',
      views: '14.6K',
      date: 'Dec 2024'
    },
    {
      id: 9,
      title: 'The Power of Kingdom Economics',
      description: 'Discover how biblical principles of stewardship and generosity create sustainable wealth and impact. Learn to align your financial decisions with kingdom values.',
      category: 'wealth',
      type: 'insight',
      readTime: '7 min read',
      thumbnail: 'https://readdy.ai/api/search-image?query=African%20American%20professional%20studying%20bible%20and%20financial%20documents%2C%20elegant%20study%20room%2C%20spiritual%20wealth%20atmosphere%2C%20golden%20lighting&width=800&height=450&seq=insight-kingdom&orientation=landscape',
      views: '11.2K',
      date: 'Dec 2024'
    },
    {
      id: 10,
      title: 'Marriage as Partnership: Building Together',
      description: 'Explore how to build a thriving marriage partnership where both individuals grow, contribute, and create legacy together. Practical insights for modern couples.',
      category: 'relationships',
      type: 'insight',
      readTime: '9 min read',
      thumbnail: 'https://readdy.ai/api/search-image?query=Diverse%20professional%20couple%20working%20together%20in%20elegant%20home%20office%2C%20partnership%20atmosphere%2C%20warm%20lighting%2C%20successful%20marriage%20setting&width=800&height=450&seq=insight-marriage&orientation=landscape',
      views: '10.5K',
      date: 'Nov 2024'
    }
  ];

  const filteredResources = resources.filter(resource => {
    const categoryMatch = selectedCategory === 'all' || resource.category === selectedCategory;
    const typeMatch = selectedType === 'all' || resource.type === selectedType;
    return categoryMatch && typeMatch;
  });

  const handleResourceClick = (resource: any) => {
    if (resource.type === 'video') {
      setSelectedResource(resource);
    }
  };

  const handleSubmissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formData = new FormData();
      Object.entries(submissionForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4g3m0s65ccm3ui4r2n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubmissionForm({
          name: '',
          email: '',
          contentType: 'video',
          category: 'wealth',
          title: '',
          description: '',
          url: '',
          message: ''
        });
        setTimeout(() => {
          setShowSubmissionModal(false);
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSubmissionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <>
      <SEOHead
        title="Resources | Insights & Videos | The CROWN Abundance Movement"
        description="Explore The CROWN Abundance Movement resource library featuring transformational insights, articles, videos, and masterclasses on wealth mastery, relationship wholeness, and generational legacy."
        keywords="CROWN resources, wealth mastery insights, relationship articles, legacy building, masterclasses, videos, transformational content"
        canonicalPath="/resources"
        type="CollectionPage"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#3D2C8D' }}>
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="mb-8">
              <div className="inline-block p-4 rounded-full bg-gradient-to-r from-amber-400/20 to-yellow-300/20 backdrop-blur-sm border border-amber-400/30 mb-6">
                <i className="ri-book-open-line text-4xl" style={{ color: '#F5A623' }}></i>
              </div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
              Resource
              <span className="block text-6xl lg:text-8xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                Library
              </span>
            </h1>
            
            <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-12" style={{ color: '#FFFFFF' }}>
              Access transformational insights, articles, videos, and masterclasses to empower your journey in wealth mastery, relationship wholeness, and generational legacy.
            </p>
          </div>
        </section>

        {/* Type Filter */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {types.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center ${
                    selectedType === type.id
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-lg scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-amber-400/20'
                  }`}
                >
                  <i className={`${type.icon} mr-2`}></i>
                  {type.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-4 pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center ${
                    selectedCategory === category.id
                      ? 'bg-white text-purple-900 shadow-lg scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  <i className={`${category.icon} mr-2`}></i>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className="group bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-amber-400/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
                  onClick={() => handleResourceClick(resource)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={resource.thumbnail}
                      alt={resource.title}
                      className="w-full h-48 object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                        <i className={`${resource.type === 'video' ? 'ri-play-fill' : 'ri-article-line'} text-3xl text-white`}></i>
                      </div>
                    </div>
                    <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-lg text-white text-sm font-medium flex items-center">
                      <i className={`${resource.type === 'video' ? 'ri-video-line' : 'ri-article-line'} mr-2`}></i>
                      {resource.type === 'video' ? 'Video' : 'Insight'}
                    </div>
                    <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-lg text-white text-sm font-medium">
                      {resource.duration || resource.readTime}
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 line-clamp-2" style={{ color: '#F5A623' }}>
                      {resource.title}
                    </h3>
                    <p className="text-sm mb-4 line-clamp-2" style={{ color: '#FFFFFF' }}>
                      {resource.description}
                    </p>
                    <div className="flex items-center justify-between text-sm" style={{ color: '#F5A623' }}>
                      <div className="flex items-center">
                        <i className="ri-eye-line mr-2"></i>
                        {resource.views} views
                      </div>
                      <div className="flex items-center">
                        <i className="ri-calendar-line mr-2"></i>
                        {resource.date}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {selectedResource && selectedResource.type === 'video' && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedResource(null)}
          >
            <div 
              className="relative w-full max-w-4xl bg-purple-900 rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedResource(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              
              <div className="aspect-video">
                <iframe
                  src={selectedResource.videoUrl}
                  title={selectedResource.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#F5A623' }}>
                  {selectedResource.title}
                </h3>
                <p className="text-sm" style={{ color: '#FFFFFF' }}>
                  {selectedResource.description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-3xl p-12 text-white">
              <h3 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Want to Contribute?
              </h3>
              <p className="text-xl mb-8 text-white/90">
                Share your transformational insights or video testimonials to inspire and empower the CROWN community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowSubmissionModal(true)}
                  className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-upload-cloud-line mr-2"></i>
                  Submit Content
                </button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-amber-600 px-8 py-4 rounded-lg font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-information-line mr-2"></i>
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Content Submission Modal */}
        {showSubmissionModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowSubmissionModal(false)}
          >
            <div 
              className="relative w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubmissionModal(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              
              <div className="bg-gradient-to-r from-amber-600 to-yellow-500 p-8 text-white">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-upload-cloud-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Submit Your Content</h3>
                    <p className="text-white/90">Share your insights with the CROWN community</p>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleSubmissionSubmit} className="p-8" data-readdy-form id="content-submission">
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 rounded-lg text-green-700">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! Your content submission has been received successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 rounded-lg text-red-700">
                    <i className="ri-error-warning-line mr-2"></i>
                    There was an error submitting your content. Please try again.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={submissionForm.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={submissionForm.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Content Type *
                    </label>
                    <div className="relative">
                      <select
                        name="contentType"
                        value={submissionForm.contentType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="video">Video</option>
                        <option value="insight">Insight/Article</option>
                        <option value="testimonial">Testimonial</option>
                        <option value="resource">Resource</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category *
                    </label>
                    <div className="relative">
                      <select
                        name="category"
                        value={submissionForm.category}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="wealth">Wealth Mastery</option>
                        <option value="relationships">Relationships</option>
                        <option value="legacy">Legacy</option>
                        <option value="testimonials">Testimonials</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Content Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={submissionForm.title}
                    onChange={handleInputChange}
                    required
                    maxLength={100}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder="Enter a compelling title for your content"
                  />
                  <p className="text-xs text-gray-500 mt-1">{submissionForm.title.length}/100 characters</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Content Description *
                  </label>
                  <textarea
                    name="description"
                    value={submissionForm.description}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                    placeholder="Provide a detailed description of your content and its key insights..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{submissionForm.description.length}/500 characters</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Content URL
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={submissionForm.url}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder="https://youtube.com/watch?v=... or link to your article"
                  />
                  <p className="text-xs text-gray-500 mt-1">If available, provide a direct link to your content</p>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Message
                  </label>
                  <textarea
                    name="message"
                    value={submissionForm.message}
                    onChange={handleInputChange}
                    maxLength={300}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                    placeholder="Share any additional context or special instructions..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{submissionForm.message.length}/300 characters</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 text-white py-4 px-6 rounded-lg font-semibold hover:from-amber-700 hover:to-yellow-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Submit Content
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setShowSubmissionModal(false)}
                    className="px-6 py-4 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
